import React from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { supabase } from "../../components/SupabaseClient";
import AuthShell from "./components/AuthShell.jsx";
import AuthBrandPanel from "./components/AuthBrandPanel.jsx";
import AuthFormHeader from "./components/AuthFormHeader.jsx";
import AuthField from "./components/AuthField.jsx";
import {
  AUTH_BRAND_CONTENT,
  AUTH_FORM_HEADERS,
} from "./components/authContents";
import { primaryBtnSx } from "./components/authStyles";
import PasswordRulesDisplay from "./components/PasswordRulesDisplay.jsx";
import { runPasswordRules } from "../../utils/userCredentialValidation";

// Centered single-message states (checking / no session) on the same canvas.
function CenteredMessage({ children }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        bgcolor: "#EEF1F0",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 480 }}>{children}</Box>
    </Box>
  );
}

function ResetPassword() {
  const navigate = useNavigate();

  const [showPwd, setShowPwd] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const [passwordValue, setPasswordValue] = React.useState("");
  const [confirmValue, setConfirmValue] = React.useState("");

  const [passwordError, setPasswordError] = React.useState("");
  const [confirmError, setConfirmError] = React.useState("");
  const [submitError, setSubmitError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const [hasSession, setHasSession] = React.useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = React.useState(false);

  React.useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setHasSession(!!session);
    };
    checkSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const rules = runPasswordRules(passwordValue);
    const firstFailed = rules.find((r) => !r.valid);
    const confirmErr =
      passwordValue !== confirmValue ? "Passwords do not match." : "";

    setPasswordError(firstFailed ? firstFailed.message : "");
    setConfirmError(confirmErr);

    if (firstFailed || confirmErr) {
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({
      password: passwordValue,
    });
    setSubmitting(false);

    if (updateError) {
      setSubmitError(updateError.message);
      return;
    }

    setSuccessDialogOpen(true);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    navigate("/signin");
  };

  if (hasSession === null) {
    return (
      <CenteredMessage>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={22} />
          <Typography>Checking session…</Typography>
        </Box>
      </CenteredMessage>
    );
  }

  if (hasSession === false) {
    return (
      <CenteredMessage>
        <Alert severity="error">
          No active session detected. Please open the password reset link from
          your profile page or from the password reset email again.
        </Alert>
      </CenteredMessage>
    );
  }

  const passwordRulesState = runPasswordRules(passwordValue);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        bgcolor: "#EEF1F0",
      }}
    >
      <AuthShell
        brand={<AuthBrandPanel {...AUTH_BRAND_CONTENT.resetPassword} />}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <AuthFormHeader {...AUTH_FORM_HEADERS.resetPassword} />

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <AuthField
            label="New Password"
            name="password"
            placeholder="Enter new password"
            value={passwordValue}
            onChange={(e) => {
              const val = e.target.value;
              setPasswordValue(val);

              const rules = runPasswordRules(val);
              const firstFailed = rules.find((r) => !r.valid);
              setPasswordError(firstFailed ? firstFailed.message : "");

              if (confirmValue) {
                setConfirmError(
                  val !== confirmValue ? "Passwords do not match." : "",
                );
              }
            }}
            error={passwordError}
            startIcon={<LockOutlinedIcon fontSize="small" />}
            password
            showPassword={showPwd}
            onTogglePassword={() => setShowPwd((v) => !v)}
          />

          <PasswordRulesDisplay rules={passwordRulesState} />

          <AuthField
            label="Confirm Password"
            labelProps={{ mt: 2 }}
            name="confirmPassword"
            placeholder="Re-enter password"
            value={confirmValue}
            onChange={(e) => {
              const val = e.target.value;
              setConfirmValue(val);
              setConfirmError(
                passwordValue !== val ? "Passwords do not match." : "",
              );
            }}
            error={confirmError}
            startIcon={<LockOutlinedIcon fontSize="small" />}
            password
            showPassword={showConfirm}
            onTogglePassword={() => setShowConfirm((v) => !v)}
            toggleAriaLabel="toggle confirm password visibility"
          />

          <Button
            type="submit"
            fullWidth
            disabled={submitting}
            sx={primaryBtnSx}
            startIcon={
              submitting ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : null
            }
          >
            {submitting ? "Resetting…" : "Reset Password"}
          </Button>

          <Typography align="center" variant="body2" sx={{ mt: 3 }}>
            Back to{" "}
            <Link
              component={RouterLink}
              to="/signin"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </AuthShell>

      <Dialog open={successDialogOpen} onClose={handleSuccessDialogClose}>
        <DialogTitle>Password reset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your password has been reset successfully. Please sign in with your
            new password.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSuccessDialogClose}
            autoFocus
            variant="outlined"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ResetPassword;
